import type { PatientReferralType } from 'app/services/metrics'
import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { defer as deferAsync, uniq } from 'lodash'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Combo from 'app/components/ui/combo/combo'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import { ContactsByReferralType } from 'app/components/settings/mapping/ContactsByReferralType'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useHandleSearchParams, useWorkspace } from 'app/utils'
import { cleanName } from 'app/utils/cleanName'
import { titleCase } from 'app/utils/titleCase'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import type {
  CreatePmsReferralMappingMutation,
  PmsReferralMappingsQuery,
  UpdatePmsReferralMappingMutation,
} from 'app/generated/gql/graphql'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'

// Referral-type channel mappings are the legacy ChannelMapping table, served as
// PmsReferralMapping (the channelMapping* operations serve the source/medium
// ChannelMappingV2 model).
const PMS_REFERRAL_MAPPINGS_QUERY = graphql(`
  query PmsReferralMappings($pagination: PaginationInput) {
    pmsReferralMappings(pagination: $pagination) {
      hits {
        id
        referralType
        channel
        source
        medium
        campaign
        force
      }
      total
    }
  }
`)

const CREATE_PMS_REFERRAL_MAPPING_MUTATION = graphql(`
  mutation CreatePmsReferralMapping($input: CreatePmsReferralMappingInput!) {
    createPmsReferralMapping(input: $input) {
      id
      referralType
      channel
      source
      medium
      campaign
      force
    }
  }
`)

const UPDATE_PMS_REFERRAL_MAPPING_MUTATION = graphql(`
  mutation UpdatePmsReferralMapping($id: ID!, $input: UpdatePmsReferralMappingInput!) {
    updatePmsReferralMapping(id: $id, input: $input) {
      id
      referralType
      channel
      source
      medium
      campaign
      force
    }
  }
`)

type StoredMapping = PmsReferralMappingsQuery[`pmsReferralMappings`][`hits`][number]

const PATIENTS_LIST_QUERY = graphql(`
  query PatientsReferralTypes($search: String, $pagination: PaginationInput) {
    patients(search: $search, pagination: $pagination) {
      hits {
        id
        referralType
        locationId
      }
      total
    }
  }
`)

const PATIENTS_COUNT_QUERY = graphql(`
  query PatientsCount($search: String, $pagination: PaginationInput) {
    patients(search: $search, pagination: $pagination) {
      total
    }
  }
`)

import { debug, logError } from 'lib/logging'

function ReferralCountCell({
  row,
  onCount,
}: {
  row: PatientReferralType
  onCount: (referralType: string, count: number) => void
}) {
  const [{ data, fetching }] = useQuery({
    query: PATIENTS_COUNT_QUERY,
    variables: { search: `referralType:"${row.referralType.replace(/"/g, '\\"')}"`, pagination: { limit: 1 } },
  })
  const total = data?.patients?.total ?? 0

  useEffect(() => {
    if (!fetching && data) onCount(row.referralType, total)
  }, [fetching, total])

  if (fetching)
    return (
      <div className="flex items-center justify-between px-2">
        <span className="inline-block h-4 w-8 animate-pulse rounded bg-surface-strong" />
      </div>
    )
  return (
    <div className="flex items-center justify-between px-2">
      <span className="cursor-default overflow-hidden overflow-ellipsis whitespace-nowrap">
        {total.toLocaleString()}
      </span>
    </div>
  )
}

function LocationsCell(props: { locationIds: string[] }) {
  return (
    <div className="flex h-full flex-nowrap overflow-auto">
      {props.locationIds?.map((locationId: string) => (
        <LazyLocationCell key={locationId} locationId={locationId} className="tag m-1 px-2" loadingIndicator={false} />
      ))}
    </div>
  )
}

export type UsePmsReferralReturn = {
  filteredRows: PatientReferralType[] | undefined
  deduplicatedReferralTypes: PatientReferralType[] | undefined
  defaultColumns: ColumnDef<PatientReferralType, any>[]
  isSaving: boolean
  onSave: (e: FormEvent) => void
  onSearchValueChange: (searchValue: string) => void
  onScroll: React.UIEventHandler<HTMLDivElement>
}

import { FilterValueType } from 'app/filter.utils'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'

const filterableColumns = [
  {
    attribute: `locationIds`,
    displayName: `Locations`,
    type: FilterValueType.id,
    optionsSource: `/api/locations`,
  },
]

type PatientHit = { id: string; referralType: string | null; locationId: string }

export function usePmsReferral(): UsePmsReferralReturn {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const workspace = useWorkspace()
  const [searchValue, setSearchValue] = useState<string>()
  const [isSaving, setIsSaving] = useState(false)
  const [patientPage, setPatientPage] = useState(0)
  const [hasMorePatients, setHasMorePatients] = useState(true)
  const [pages, setPages] = useState<PatientHit[][]>([])
  const [storedMappings, setStoredMappings] = useState<Map<string, StoredMapping>>()
  const countsRef = useRef<Map<string, number>>(new Map())

  const onReferralCount = useCallback((referralType: string, count: number) => {
    countsRef.current.set(referralType, count)
  }, [])

  const [{ data, fetching }] = useQuery({
    query: PATIENTS_LIST_QUERY,
    variables: { search: 'referralType:*', pagination: { page: patientPage, limit: 1000 } },
  })

  useEffect(() => {
    gqlQuery<PmsReferralMappingsQuery>(PMS_REFERRAL_MAPPINGS_QUERY, { pagination: { limit: 1000 } }).then(
      ({ data, error }) => {
        if (error) logError(`Error fetching referral mappings`, error)
        const hits = data?.pmsReferralMappings.hits ?? []
        setStoredMappings(new Map(hits.map((hit) => [cleanName(hit.referralType ?? ``), hit])))
      },
    )
  }, [])

  // Minimal side effect: just store raw page results
  useEffect(() => {
    if (data?.patients?.hits && pages.length === patientPage) {
      setPages((prev) => [...prev, data.patients.hits])
      setHasMorePatients((patientPage + 1) * 1000 < data.patients.total)
    }
  }, [data])

  // Pure computation: group by referralType
  const referralTypes = useMemo(() => {
    if (pages.length === 0) return undefined
    const grouped = new Map<string, PatientReferralType>()
    for (const page of pages) {
      for (const row of page) {
        if (!row.referralType) continue
        const existing = grouped.get(row.referralType)
        if (existing) {
          if (row.locationId && !existing.locationIds.includes(row.locationId)) {
            existing.locationIds.push(row.locationId)
          }
        } else {
          grouped.set(row.referralType, {
            referralType: row.referralType,
            count: 0,
            locationIds: row.locationId ? [row.locationId] : [],
          })
        }
      }
    }
    return Array.from(grouped.values())
  }, [pages])

  const deduplicatedReferralTypes = useMemo(() => {
    if (!referralTypes || !storedMappings) return undefined
    const grouped = new Map<string, PatientReferralType>()
    referralTypes.forEach((row) => {
      const cleanedKey = cleanName(row.referralType ?? ``)
      if (!cleanedKey) return
      const existing = grouped.get(cleanedKey)
      if (existing) {
        existing.locationIds = uniq([...existing.locationIds, ...row.locationIds])
        existing.count += row.count
      } else {
        const stored = storedMappings.get(cleanedKey)
        grouped.set(cleanedKey, {
          ...row,
          referralType: row.referralType?.replace(/,\s*$/, ``),
          mappingId: stored?.id,
          channel: stored?.channel,
          source: stored?.source,
          medium: stored?.medium,
          campaign: stored?.campaign,
          force: stored?.force,
        })
      }
    })
    return Array.from(grouped.values())
  }, [referralTypes, storedMappings])

  const filteredRows = useClientSideTableFilters(
    deduplicatedReferralTypes,
    filterableColumns,
    `referralType`,
    [`referralType`],
    searchValue,
  )

  const onScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    const el = event.target as HTMLDivElement
    if (!fetching && hasMorePatients && el.clientHeight + el.scrollTop >= el.scrollHeight - 400) {
      setPatientPage((prev) => prev + 1)
    }
  }

  useEffect(() => {
    if (searchParams.has(`referralType`)) {
      const deferId = deferAsync(() =>
        showModal(<ContactsByReferralType referralType={searchParams.get(`referralType`)!} />, {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          onClose: () => handleSearchParams({ referralType: undefined }),
          useRadix: true,
        }),
      )
      return () => clearTimeout(deferId)
    }
  }, [])

  const saveRow = async (row: PatientReferralType) => {
    if (row.mappingId) {
      const { error } = await gqlMutation(UPDATE_PMS_REFERRAL_MAPPING_MUTATION, {
        id: row.mappingId,
        input: { channel: row.channel },
      })
      if (error) throw error
    } else {
      const { data, error } = await gqlMutation(CREATE_PMS_REFERRAL_MAPPING_MUTATION, {
        input: { referralType: row.referralType, channel: row.channel },
      })
      if (error) throw error
      row.mappingId = data?.createPmsReferralMapping.id
    }
    row.changed = false
  }

  const onSave = async (e: FormEvent) => {
    e.preventDefault()
    const changedRows = deduplicatedReferralTypes?.filter((r) => r.changed) ?? []
    debug(`channel`, `changes`, changedRows)
    setIsSaving(true)
    try {
      for (const row of changedRows) await saveRow(row)
      notifySuccess(`Save Successful. It may take a few minutes for your changes to take effect.`)
    } catch (err) {
      logError(`Error saving referral mappings`, err)
      notifyError(`Could not save referral mappings. Please try again.`)
    } finally {
      setIsSaving(false)
    }
  }

  const onSearchValueChange = (searchValue: string) => {
    setSearchValue(searchValue)
  }

  const defaultColumns = useMemo(
    () =>
      [
        {
          id: `Referral Type`,
          accessorFn: (row) => titleCase(row.referralType),
          header: `Referral Type`,
          meta: {
            defaultVisible: true,
            pinned: true,
            onOpen(row) {
              const count = countsRef.current.get(row.referralType) ?? row.count
              handleSearchParams({ referralType: row.referralType })
              showModal(<ContactsByReferralType referralType={row.referralType} count={count} />, {
                hideTitle: true,
                removePadding: true,
                isFullScreen: true,
                onClose: () => handleSearchParams({ referralType: undefined }),
                useRadix: true,
              })
            },
          },
          enableSorting: true,
        },
        {
          id: `Channel`,
          header: `Channel`,
          accessorFn: (row) => row.channel,
          meta: {
            defaultVisible: true,
            tooltipClassName: `overflow-visible`,
            ControlledComponent: (row) => {
              const [channel, setChannel] = useState(
                row?.channel ? { name: row.channel, value: row.channel, id: row.channel } : undefined,
              )
              return (
                <Combo
                  inputClassName="!h-full !border-0 !shadow-none !p-2 text-body-sm text-strong"
                  placeholder=""
                  options={[
                    { name: `Digital Marketing`, value: `Digital Marketing`, id: `Digital Marketing` },
                    { name: `Referral Marketing`, value: `Referral Marketing`, id: `Referral Marketing` },
                    { name: `Traditional Marketing`, value: `Traditional Marketing`, id: `Traditional Marketing` },
                    { name: `None`, value: null, id: `None` },
                  ]}
                  value={channel}
                  onChange={(option) => {
                    setChannel(option)
                    row!.channel = option.value
                    row!.changed = true
                  }}
                />
              )
            },
          },
          enableSorting: false,
        },
        {
          id: `Count`,
          header: `Count`,
          accessorFn: (row) => row.count,
          meta: {
            defaultVisible: true,
            ControlledComponent: (row: PatientReferralType | undefined) =>
              row ? <ReferralCountCell row={row} onCount={onReferralCount} /> : null,
          },
          enableSorting: true,
        },
        {
          accessorFn: (row) => row.locationIds,
          id: `Locations`,
          cell: (props) => <LocationsCell locationIds={props.getValue()} />,
          header: `Locations`,
          meta: { defaultVisible: true },
          enableSorting: false,
        },
      ] as ColumnDef<PatientReferralType, any>[],
    [workspace],
  )

  return {
    filteredRows,
    deduplicatedReferralTypes,
    defaultColumns,
    isSaving,
    onSave,
    onSearchValueChange,
    onScroll,
  }
}

export { filterableColumns as pmsReferralFilterableColumns }
